import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '@/shared/lib/cn'
import type { IconName } from '@/shared/ui/icon'
import { Icon } from '@/shared/ui/icon'
import s from './DropdownMenu.module.css'

type DropdownMenuProps = {
  trigger: ReactNode
  children: ReactNode
  align?: 'start' | 'end'
  width?: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Панель — тот же вид, что `Popover` (Step 3 брифа), поведение — навигация стрелками между
 * пунктами, которую даёт Radix `DropdownMenu` (нужна для меню блока контента, «⋮»,
 * prototype/Prototype.dc.html:847). `data-align` не дублируется вручную — та же причина, что и
 * в `Popover` (см. комментарий там): `DropdownMenu.Content` тоже строится поверх
 * `Popper.Content`, который сам проставляет `data-side`/`data-align` по факту размещения.
 */
function DropdownMenuRoot({
  trigger,
  children,
  align = 'start',
  width,
  open,
  onOpenChange,
}: DropdownMenuProps) {
  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenuPrimitive.Trigger asChild>{trigger}</DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          className={s.content}
          align={align}
          sideOffset={8}
          style={width ? { width } : undefined}
        >
          {children}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  )
}

type DropdownMenuItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  icon?: IconName
  danger?: boolean
}

function Item({ icon, danger, className, children, ...rest }: DropdownMenuItemProps) {
  return (
    <DropdownMenuPrimitive.Item className={cn(s.item, danger && s.danger, className)} {...rest}>
      {/* `dangerIcon` — только у деструктивного пункта: под курсором текст уходит в `--ink` ради
          контраста, а иконка остаётся красной. См. DropdownMenu.module.css. */}
      {icon && <Icon name={icon} size={15} className={cn(danger && s.dangerIcon)} />}
      {children}
    </DropdownMenuPrimitive.Item>
  )
}

function Separator({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>) {
  return <DropdownMenuPrimitive.Separator className={cn(s.separator, className)} {...rest} />
}

/** `<DropdownMenu trigger={...}>` — корень; `DropdownMenu.Item`/`DropdownMenu.Separator` — пункты. */
export const DropdownMenu = Object.assign(DropdownMenuRoot, { Item, Separator })
